import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Project } from 'app/modules/projects/projects.types';
import { Dictionary } from 'app/shared/models/dictionary';
import { Disposition, DispositionGroup } from '../../types/dispositions.types';
import { AddDispositionsComponent } from '../add-dispositions/add-dispositions.component';
import { FeatureFlag, NewPermission } from '@core/enums';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { AccountState } from '@state/account/state';
import { Store } from '@ngxs/store';
import { TableStatusRenderer } from '@shared/components/ts-table-renderers/table-status-renderer/table-status-renderer.component';
import { UserState } from '@state/user/state';
import { TitleCasePipe } from '@angular/common';

@Component({
  selector: 'app-dispositions-table',
  templateUrl: './dispositions-table.component.html',
  styleUrls: ['./dispositions-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DispositionsTableComponent extends OnDestroyMixin implements OnInit {
  @Input() dispositionGroup: DispositionGroup;
  @Input() project: Project;
  @Input() options: Dictionary<any[]>;
  @Input() showActionsColumn = false;
  @Output() onOpenFilesAndCommentsDrawer = new EventEmitter<Disposition>();
  @Output() onDispositionEditedUpdated = new EventEmitter<{ disposition: Disposition }>();

  displayColumns = [
    'name',
    'transaction_type',
    'workflow_state',
    'offer_price',
    'due_diligence_period',
    'closing_date',
  ];
  NewPermission = NewPermission;

  columnDefs: ColumnDef[] = [];
  actions: TableActions[] = [];
  newTable: boolean;

  get dispositions(): Disposition[] {
    return this.dispositionGroup?.disposition_group_transactions?.map((x) => {
      return { ...x, transaction_type: new TitleCasePipe().transform(x.transaction_type) };
    });
  }

  constructor(
    private readonly matDialog: MatDialog,
    private readonly store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.checkTable();
    if (this.showActionsColumn) {
      this.displayColumns = [...this.displayColumns, 'actions'];
    }
    if (this.newTable) {
      this.setColDefs();
    }
  }

  openEditDispositionModel(disposition: Disposition, group: DispositionGroup): void {
    const dialogRef = this.matDialog.open(AddDispositionsComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: {
        project: this.project,
        options: this.options,
        disposition,
        group,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((disposition: { disposition: Disposition }) => {
        if (disposition) {
          this.onDispositionEditedUpdated.emit(disposition);
        }
      });
  }

  openFilesAndCommentsDrawer(disposition: Disposition): void {
    this.onOpenFilesAndCommentsDrawer.emit(disposition);
  }

  setColDefs(): void {
    this.columnDefs = [
      {
        label: 'Company',
        name: 'name',
        type: 'text',
        tsTableOptions: {
          headerName: 'Company',
          field: 'name',
          editable: false,
        },
      },
      {
        label: 'Type',
        name: 'transaction_type',
        type: 'text',
        tsTableOptions: {
          headerName: 'Type',
          field: 'transaction_type',
          editable: false,
          titleCase: true,
        },
      },
      {
        label: 'Status',
        name: 'workflow_state',
        type: 'text',
        tsTableOptions: {
          headerName: 'Status',
          field: 'workflow_state',
          editable: false,
          cellRenderer: TableStatusRenderer,
          cellRendererParams: {
            statusColors: {
              draft: '#6B7280',
              submitted: '#3F83F8',
              rejected: '#F05252',
              accepted: '#0E9F6E',
            },
          },
        },
      },
      {
        label: 'Price',
        name: 'offer_price',
        type: 'money',
        tsTableOptions: {
          headerName: 'Offer Price',
          field: 'amount',
          editable: false,
        },
      },
      {
        label: 'DD Days',
        name: 'due_diligence_period',
        type: 'number',
        tsTableOptions: {
          headerName: 'Due Diligence Period',
          field: 'due_diligence_period',
          editable: false,
        },
      },
      {
        label: 'Closing Days',
        name: 'closing_date',
        type: 'number',
        tsTableOptions: {
          headerName: 'Closing Date',
          field: 'closing_period',
          editable: false,
        },
      },
    ];
    this.setTableActions();
  }

  setTableActions(): void {
    if (this.showActionsColumn) {
      this.actions = [
        {
          label: 'Files and Comments',
          action: (item) => this.openFilesAndCommentsDrawer(item),
          icon: 'chevron_right',
        },
      ];

      if (UserState.hasPermission(NewPermission.DISPOSITIONS_EDIT)) {
        this.actions.push({
          label: 'Edit',
          action: (item) => this.openEditDispositionModel(item, this.dispositionGroup),
          icon: 'edit',
        });
      }
    }
  }

  checkTable() {
    this.newTable = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_TABLE));
  }
}
